import { useState, useRef, useEffect, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@vitness/theme';

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: 'right' | 'bottom';
  delay?: number;
  /** Style merged onto the trigger wrapper (e.g. to make it fill its row). */
  style?: CSSProperties;
}

export function Tooltip({ content, children, side = 'right', delay = 200, style }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const { accentRgb } = useTheme();
  const rs = `${accentRgb.r},${accentRgb.g},${accentRgb.b}`;

  const show = () => {
    timeout.current = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        if (side === 'right') {
          setPos({ top: rect.top + rect.height / 2, left: rect.right + 8 });
        } else {
          setPos({ top: rect.bottom + 6, left: rect.left + rect.width / 2 });
        }
      }
      setVisible(true);
    }, delay);
  };
  const hide = () => {
    clearTimeout(timeout.current);
    setVisible(false);
  };

  useEffect(() => () => clearTimeout(timeout.current), []);

  const transform = side === 'right' ? 'translateY(-50%)' : 'translateX(-50%)';

  return (
    <div
      ref={triggerRef}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      style={{ position: 'relative', display: 'inline-flex', ...style }}
    >
      {children}
      {visible && createPortal(
        <div
          role="tooltip"
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            transform,
            zIndex: 9999,
            padding: '5px 10px',
            borderRadius: 6,
            background: `rgba(${rs},0.12)`,
            backdropFilter: 'blur(12px)',
            border: '1px solid var(--vn-border-default)',
            fontFamily: 'var(--vn-font-body)',
            fontSize: 11,
            fontWeight: 500,
            color: 'var(--vn-text-primary)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          }}
        >
          {content}
        </div>,
        document.body,
      )}
    </div>
  );
}
